// ═══════════════════════════════════════════════════════════════════
//  modules/osf/OsfPage.js
//  OsfPage · Spawn · santé et détections
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { erreurDe, horodate } from "../ah/ah";
import { dateCourte, duree, nombre } from "../erp/metiers";

// ⚠ CETTE PAGE NE LIT PAS LA MÊME CHOSE QUE LES QUATRE AUTRES, ET IL FAUT
// QUE ÇA SE VOIE.
//
// OrialisAH, OrialisJobs et OrialisShop écrivent tout en base. OrialisSpawnFarm,
// non — et c'est délibéré : l'état des blocs s'écrit des centaines de fois par
// heure et reste dans un fichier local, pour qu'une panne réseau ne fige jamais
// une mine. Salles, positions, cycles, spawners, pêche, artefacts : tout vit
// sur le disque de Minestrator, hors de portée de l'intranet.
//
// D'où deux onglets qui n'ont pas la même nature :
//   Santé      → lit le LOG DE DÉMARRAGE. Pas du temps réel : la fraîcheur
//                vaut le dernier passage de 4-LOGS.bat, et la page le dit.
//   Détections → lit la seule table que ce plugin alimente, quand elle est
//                activée. Elle ne l'est pas encore.
export function OsfPage({ token }) {
  const [vue, setVue] = useState("sante");
  const vues = [
    { id: "sante", label: "🩺 Santé du spawn" },
    { id: "recidives", label: "🔁 Récidives" },
    { id: "detections", label: "📋 Détections" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ color: THEME.text, fontSize: 22, fontWeight: 800, margin: 0 }}>⛏️ Spawn</h1>
        <span style={{ color: THEME.textMuted, fontSize: 12 }}>
          OrialisSpawnFarm — la santé vient des logs, pas de la base
        </span>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${THEME.border}`, flexWrap: "wrap" }}>
        {vues.map((v) => (
          <button key={v.id} onClick={() => setVue(v.id)}
            style={{
              background: vue === v.id ? THEME.panel : "transparent",
              border: `1px solid ${vue === v.id ? THEME.border : "transparent"}`,
              borderBottom: vue === v.id ? `1px solid ${THEME.panel}` : `1px solid ${THEME.border}`,
              color: vue === v.id ? THEME.text : THEME.textMuted,
              borderRadius: "6px 6px 0 0", padding: "8px 16px", cursor: "pointer",
              fontSize: 13, fontWeight: vue === v.id ? 600 : 400, marginBottom: -1,
            }}>{v.label}</button>
        ))}
      </div>

      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: "0 8px 8px 8px", padding: 20, minHeight: 260 }}>
        {vue === "sante" && <Sante token={token} />}
        {vue === "recidives" && <Recidives token={token} />}
        {vue === "detections" && <Detections token={token} />}
      </div>
    </div>
  );
}

const champ = {
  background: THEME.surface, border: `1px solid ${THEME.border}`,
  color: THEME.text, borderRadius: 6, padding: "7px 10px", fontSize: 12,
};
const th = {
  color: THEME.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
  textAlign: "left", padding: "6px 8px", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap",
};
const td = { color: THEME.text, fontSize: 12, padding: "6px 8px", borderBottom: `1px solid ${THEME.border}` };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

function pluriel(n, singulier, plurielMot) {
  return nombre(n) + " " + (Math.abs(Number(n)) > 1 ? (plurielMot || singulier + "s") : singulier);
}

function Erreur({ texte }) {
  return (
    <div style={{ color: THEME.red, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>⛔ La route n'a pas répondu correctement</div>
      <code style={{ color: THEME.text, fontSize: 12, wordBreak: "break-word" }}>{texte}</code>
    </div>
  );
}

function Compteur({ label, valeur, detail, couleur }) {
  return (
    <div style={{
      background: THEME.surface, border: `1px solid ${couleur || THEME.border}`,
      borderRadius: 8, padding: "10px 14px", minWidth: 128,
    }}>
      <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: couleur || THEME.text, fontSize: 19, fontWeight: 800, marginTop: 2 }}>{valeur}</div>
      {detail && <div style={{ color: THEME.textMuted, fontSize: 10, marginTop: 2 }}>{detail}</div>}
    </div>
  );
}

// ── Le bandeau du journal éteint, partagé par les deux vues qui en dépendent.
// Il explique la CAUSE — une entrée de registre qui manque — au lieu de
// laisser une liste vide faire croire qu'il ne se passe rien.
function Eteint({ pourquoi }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "14px 18px", fontSize: 13, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>⏳ Le journal des détections n'est pas activé</div>
        {pourquoi}
      </div>
      <div style={{ color: THEME.textMuted, fontSize: 12, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "12px 16px", lineHeight: 1.6 }}>
        <b style={{ color: THEME.textDim }}>Ce qui manque n'est pas du code.</b> Le plugin refuse de
        collecter tant que le traitement n'est pas au registre — et il a raison de refuser.
        L'ordre est&nbsp;:
        <ol style={{ margin: "8px 0 0 18px", padding: 0 }}>
          <li>relire <code>RGPD_Entree_9_Detections_OSF_05-09-2026.md</code> ;</li>
          <li>ajouter la ligne n° 9 au registre et le paragraphe au règlement ;</li>
          <li>passer <code>journal.actif: true</code> et remplir <code>database</code>,
              <b> à la main</b> dans le <code>config.yml</code> du spawn — le jar ne réécrit
              jamais un <code>config.yml</code> qui existe déjà ;</li>
          <li>redémarrer, et vérifier que le log dit <code>Journal (§ 10.4) : actif</code>.</li>
        </ol>
        <div style={{ marginTop: 8 }}>
          <b style={{ color: THEME.textDim }}>Pendant ce temps l'anti-spam fonctionne</b> — il
          avertit et téléporte. Ce qui est éteint, c'est uniquement la trace écrite.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  SANTÉ — lue dans le log de démarrage
// ═══════════════════════════════════════════════════════════════════════
function Sante({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/osf/sante`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setData).catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Lecture du log...</div>;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  if (data.indisponible) {
    return (
      <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>⏳ Rien à lire</div>
        {data.indisponible}
        <div style={{ marginTop: 8, color: THEME.textMuted, fontSize: 12 }}>
          Dossier surveillé : <code>{data.dossier}</code>
        </div>
      </div>
    );
  }

  const f = data.fichier || {};
  const recupere = f.recupere_le ? new Date(f.recupere_le) : null;
  const heures = recupere ? (Date.now() - recupere.getTime()) / 3600000 : null;
  // Au-delà d'un jour, le log ne décrit plus forcément le serveur qui tourne.
  const vieux = heures !== null && heures > 24;
  const alertes = data.alertes || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Ce que cette page est, dit avant les chiffres ─────────────── */}
      <div style={{
        color: vieux ? THEME.amber : THEME.textMuted,
        background: vieux ? THEME.amberDim : THEME.surface,
        border: `1px solid ${vieux ? THEME.amber : THEME.border}`,
        borderRadius: 8, padding: "10px 14px", fontSize: 12, lineHeight: 1.5,
      }}>
        <b style={{ color: vieux ? THEME.amber : THEME.textDim }}>
          {vieux ? "⚠ Ce log a plus d'un jour." : "Ceci n'est pas du temps réel."}
        </b>{" "}
        Ces lignes sont celles du <b>dernier démarrage</b> du spawn, telles que le plugin les a
        écrites. Elles valent ce que vaut le dernier passage de <code>4-LOGS.bat</code> :{" "}
        <code>{f.nom}</code>, récupéré {recupere ? horodate(recupere) : "—"}
        {heures !== null && <> (il y a {duree(heures * 3600)})</>}.
        {" "}Démarrage à <b>{data.demarrage}</b> — heure du serveur de jeu, écrite telle quelle.
        <div style={{ marginTop: 4 }}>
          OrialisSpawnFarm garde tout le reste — salles, positions, cycles, pêche, artefacts —
          dans des fichiers sur Minestrator, hors de portée de l'intranet. <b>Le log est la seule
          fenêtre.</b>
        </div>
      </div>

      {/* ── Les alertes que le plugin signale LUI-MÊME ────────────────── */}
      {alertes.length === 0 ? (
        <div style={{ color: THEME.emerald, background: THEME.emeraldDim, border: `1px solid ${THEME.emerald}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
          ✅ Aucun avertissement au dernier démarrage.
        </div>
      ) : (
        <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            ⚠ {pluriel(alertes.length, "avertissement")} au dernier démarrage
          </div>
          {/* On ne reformule pas : c'est le plugin qui marque ses anomalies d'un
              « ⚠ », et on remonte ce qu'il écrit. Une alerte qu'il apprendra à
              émettre demain remontera sans qu'on touche à cet écran. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {alertes.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <code style={{ color: THEME.textMuted, fontSize: 10 }}>{a.sujet.split(":")[0].trim()}</code>
                <span style={{ color: THEME.text, fontSize: 12 }}>{a.alerte}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Les indicateurs ──────────────────────────────────────────── */}
      <div>
        <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>
          Ce que le plugin a annoncé
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 8 }}>
          {(data.indicateurs || []).map((i) => {
            // ⚠ « Pas trouvé » n'est PAS zéro. Une ligne absente du log veut dire
            // que le plugin ne l'a pas écrite — pas que la valeur vaut zéro. Les
            // confondre est exactement l'erreur qui coûte une demi-journée.
            const zero = i.trouve && (i.valeur === 0 || i.valeur === "eteint");
            const couleur = !i.trouve ? THEME.textMuted : zero ? THEME.amber : THEME.text;
            return (
              <div key={i.cle} title={i.ligne || "ligne absente du log"}
                style={{
                  background: THEME.surface,
                  border: `1px solid ${!i.trouve ? THEME.border : zero ? THEME.amber : THEME.border}`,
                  borderRadius: 8, padding: "9px 12px",
                }}>
                <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                  {i.libelle}
                </div>
                <div style={{ color: couleur, fontSize: 15, fontWeight: 700, marginTop: 2, wordBreak: "break-word" }}>
                  {i.trouve ? String(i.valeur) : "ligne absente"}
                </div>
                {i.extra && <div style={{ color: THEME.textMuted, fontSize: 10 }}>{i.extra}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Le log brut, parce qu'un résumé cache toujours quelque chose ─ */}
      <details>
        <summary style={{ color: THEME.textMuted, fontSize: 12, cursor: "pointer", userSelect: "none" }}>
          Les {nombre((data.lignes || []).length)} lignes du plugin, telles quelles
        </summary>
        <pre style={{
          background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6,
          padding: 12, marginTop: 8, overflowX: "auto", fontSize: 11,
          color: THEME.textDim, whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {(data.lignes || []).map((l) => `[${l.heure || "--:--:--"}] ${l.texte}`).join("\n")}
        </pre>
      </details>

      <div>
        <button onClick={load} style={{ ...champ, cursor: "pointer" }}>↻ relire le log</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  RÉCIDIVES — la seule question que ce journal existe pour répondre
// ═══════════════════════════════════════════════════════════════════════
function Recidives({ token }) {
  const [f, setF] = useState({ mini: "3", jours: "30" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams(f);
    fetch(`${API_BASE}/api/osf/recidives?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setData).catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [token, f]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;
  if (data.eteint) return <Eteint pourquoi={data.pourquoi} />;

  const lignes = data.lignes || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      <div style={{ color: THEME.textMuted, fontSize: 12, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "10px 14px", lineHeight: 1.5 }}>
        <b style={{ color: THEME.textDim }}>Le tri se fait sur les jours, pas sur le total.</b>{" "}
        Quinze détections en une soirée, c'est une session malheureuse ou un réseau qui décroche.
        Trois détections sur trois jours différents, c'est une habitude — et c'est la seule chose
        que ce journal existe pour montrer.
        <div style={{ marginTop: 4 }}>
          Rappel : <b>le plugin ne sanctionne jamais.</b> Il avertit, puis téléporte. Ce qui suit
          n'est pas une liste de coupables, c'est une liste de gens à regarder.
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ color: THEME.textMuted, fontSize: 12 }}>au moins</label>
        <select style={champ} value={f.mini} onChange={(e) => setF({ ...f, mini: e.target.value })}>
          {["2", "3", "5", "10"].map((n) => <option key={n} value={n}>{n} détections</option>)}
        </select>
        <label style={{ color: THEME.textMuted, fontSize: 12 }}>sur</label>
        <select style={champ} value={f.jours} onChange={(e) => setF({ ...f, jours: e.target.value })}>
          {["7", "30", "90", "183"].map((n) => <option key={n} value={n}>{n} jours</option>)}
        </select>
        <span style={{ color: THEME.textMuted, fontSize: 11, marginLeft: "auto" }}>
          conservation 183 jours — au-delà, purgé
        </span>
      </div>

      {lignes.length === 0 ? (
        <div style={{ color: THEME.emerald, background: THEME.emeraldDim, border: `1px solid ${THEME.emerald}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
          ✅ Personne n'atteint {f.mini} détections sur {f.jours} jours.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Joueur</th>
                <th style={{ ...th, textAlign: "right" }} title="Le chiffre qui compte.">Jours</th>
                <th style={{ ...th, textAlign: "right" }}>Détections</th>
                <th style={{ ...th, textAlign: "right" }}>Téléporté</th>
                <th style={{ ...th, textAlign: "right" }}>Salles</th>
                <th style={{ ...th, textAlign: "right" }}>Durée moy.</th>
                <th style={th}>Première</th>
                <th style={th}>Dernière</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.uuid} style={{ background: l.jours_distincts >= 3 ? THEME.amberDim : "transparent" }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{l.pseudo || "—"}</div>
                    <code style={{ color: THEME.textMuted, fontSize: 10 }}>{l.uuid}</code>
                  </td>
                  <td style={{ ...tdNum, fontWeight: 800, color: l.jours_distincts >= 3 ? THEME.amber : THEME.text }}>
                    {nombre(l.jours_distincts)}
                  </td>
                  <td style={tdNum}>{nombre(l.detections)}</td>
                  <td style={tdNum}>{nombre(l.teleportations)}</td>
                  <td style={tdNum}>{nombre(l.salles)}</td>
                  <td style={{ ...tdNum, color: THEME.textMuted }}>{duree(l.duree_moyenne)}</td>
                  <td style={{ ...td, color: THEME.textMuted, whiteSpace: "nowrap" }}>{horodate(l.premiere)}</td>
                  <td style={{ ...td, color: THEME.textMuted, whiteSpace: "nowrap" }}>{horodate(l.derniere)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  DÉTECTIONS — le détail, ligne par ligne
// ═══════════════════════════════════════════════════════════════════════
function Detections({ token }) {
  const [f, setF] = useState({ joueur: "", salle: "", action: "", depuis: "", jusqua: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set("page", String(page));
      const res = await fetch(`${API_BASE}/api/osf/detections?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, f, page]);
  useEffect(() => { load(); }, [load]);

  const changer = (k, v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;
  if (data.eteint) return <Eteint pourquoi={data.pourquoi} />;

  const lignes = data.lignes || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...champ, minWidth: 150 }} placeholder="pseudo ou UUID..."
          value={f.joueur} onChange={(e) => changer("joueur", e.target.value)} />
        <select style={champ} value={f.salle} onChange={(e) => changer("salle", e.target.value)}>
          <option value="">toutes salles</option>
          {(data.salles || []).map((s) => (
            <option key={s.salle} value={s.salle}>{s.salle} ({s.total})</option>
          ))}
        </select>
        <select style={champ} value={f.action} onChange={(e) => changer("action", e.target.value)}>
          <option value="">avertissements et téléportations</option>
          {(data.actions || []).map((a) => (
            <option key={a.action} value={a.action}>{a.action} ({a.total})</option>
          ))}
        </select>
        <input style={champ} type="date" value={f.depuis} onChange={(e) => changer("depuis", e.target.value)} />
        <input style={champ} type="date" value={f.jusqua} onChange={(e) => changer("jusqua", e.target.value)} />
      </div>

      {data.somme && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Compteur label="Détections" valeur={nombre(data.total)} detail="sur tout le filtre" />
          <Compteur label="Joueurs" valeur={nombre(data.somme.joueurs)} />
          <Compteur label="Durée moyenne" valeur={duree(data.somme.duree_moyenne)} />
          <Compteur label="Plus ancienne" valeur={data.somme.plus_ancienne ? dateCourte(data.somme.plus_ancienne) : "—"}
            detail={`purge à ${data.conservation_jours} j`} />
        </div>
      )}

      {lignes.length === 0 ? (
        <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>
          Aucune détection ne correspond.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Début</th>
                <th style={th}>Joueur</th>
                <th style={th}>Action</th>
                <th style={th}>Salle</th>
                <th style={th}>Position</th>
                <th style={{ ...th, textAlign: "right" }}>Durée</th>
                <th style={th}>Serveur</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.id}>
                  <td style={{ ...td, color: THEME.textMuted, whiteSpace: "nowrap" }}>{horodate(l.debut)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{l.pseudo || <code style={{ fontSize: 10 }}>{l.uuid}</code>}</td>
                  <td style={td}>
                    <span style={{
                      color: l.action === "teleportation" ? THEME.amber : THEME.textMuted,
                      background: l.action === "teleportation" ? THEME.amberDim : THEME.surface,
                      border: `1px solid ${l.action === "teleportation" ? THEME.amber : THEME.border}`,
                      borderRadius: 4, padding: "1px 6px", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
                    }}>{l.action}</span>
                  </td>
                  <td style={td}>{l.salle || "—"}</td>
                  <td style={{ ...td, color: THEME.textMuted }}>
                    <code style={{ fontSize: 10 }}>{l.monde} {l.x} {l.y} {l.z}</code>
                  </td>
                  <td style={tdNum}>{duree(l.duree_s)}</td>
                  <td style={{ ...td, color: THEME.textMuted, fontSize: 11 }}>{l.serveur}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.pages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
          <button style={{ ...champ, cursor: page > 1 ? "pointer" : "default", opacity: page > 1 ? 1 : 0.4 }}
            disabled={page <= 1} onClick={() => setPage(page - 1)}>← précédent</button>
          <span style={{ color: THEME.textMuted, fontSize: 12 }}>page {data.page} / {data.pages}</span>
          <button style={{ ...champ, cursor: page < data.pages ? "pointer" : "default", opacity: page < data.pages ? 1 : 0.4 }}
            disabled={page >= data.pages} onClick={() => setPage(page + 1)}>suivant →</button>
        </div>
      )}
    </div>
  );
}
